import baseApi from 'shared/api/baseApi';

export interface MortgagePolicyEntitySummary {
  id: number;
  code: string;
  name: string;
  countryCode: string;
  isActive: boolean;
  projectId: number | null;
}

interface MortgagePolicyEntitiesResponse {
  entities: MortgagePolicyEntitySummary[];
}

export interface MortgageRequiredDocumentSpec {
  documentType: string;
  quantity: number;
  validityDays?: number;
  periodGranularity?: 'month' | 'none';
  requestedFor?: 'mainBuyer' | 'complements' | 'all';
  employmentTypes?: string[];
  isMinimumRequired?: boolean;
}

export interface MortgageEntityRequiredDocumentsRow {
  employmentType: string;
  profile: 'mainBuyer' | 'complement';
  base: MortgageRequiredDocumentSpec[];
  additional: MortgageRequiredDocumentSpec[];
}

export interface MortgageEntityRequiredDocuments {
  documents: MortgageEntityRequiredDocumentsRow[];
  additionalDocuments: MortgageRequiredDocumentSpec[];
}

const mortgageEntitiesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getMortgagePolicyEntities: build.query<MortgagePolicyEntitySummary[], void>({
      query: () => ({ url: '/admin/mortgagePolicyEntities', method: 'GET' as const }),
      transformResponse: (response: MortgagePolicyEntitiesResponse): MortgagePolicyEntitySummary[] => response.entities,
    }),
    getMortgageEntityRequiredDocuments: build.query<MortgageEntityRequiredDocuments, number>({
      query: (entityId) => ({
        url: `/backoffice/mortgage-policy-entities/${entityId}/required-documents`,
        method: 'GET' as const,
      }),
      providesTags: (_result, _error, entityId) => [{ type: 'MortgageEntityDocuments', id: entityId }],
    }),
    updateMortgageEntityAdditionalDocuments: build.mutation<
      void,
      { entityId: number; additionalDocuments: MortgageRequiredDocumentSpec[] }
    >({
      query: ({ entityId, additionalDocuments }) => ({
        url: `/admin/mortgagePolicyEntityConfigs/${entityId}`,
        method: 'POST' as const,
        body: { documentsConfig: { additionalDocuments } },
      }),
      invalidatesTags: (_result, _error, { entityId }) => [{ type: 'MortgageEntityDocuments', id: entityId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMortgagePolicyEntitiesQuery,
  useGetMortgageEntityRequiredDocumentsQuery,
  useUpdateMortgageEntityAdditionalDocumentsMutation,
} = mortgageEntitiesApi;
